import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  Divider,
  Avatar,
  Chip,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Edit,
  Lock,
  Security,
  Verified,
  Business,
  Save,
  Cancel,
  Badge as BadgeIcon,
  AdminPanelSettings,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { userService } from '../services/api';
import { UserProfile as UserProfileType, UpdatePasswordRequest } from '../types/user';
import NotificationSnackbar, { NotificationType } from '../components/NotificationSnackbar';
import { formatPhoneNumber, getPhoneNumberError } from '../utils/phoneValidation';

const UserProfile: React.FC = () => {
  const [profile, setProfile] = useState<UserProfileType | null>(null);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  });

  const [passwordData, setPasswordData] = useState<UpdatePasswordRequest>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    type: 'success' as NotificationType,
  });

  const [passwordErrors, setPasswordErrors] = useState({
    current: '',
    new: '',
    confirm: '',
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const data = await userService.getCurrentUser();
      setProfile(data);
      setFormData({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phoneNumber || '',
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      showNotification('Failed to load profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message: string, type: NotificationType = 'success') => {
    setNotification({ open: true, message, type });
  };

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    setFormData({ ...formData, phoneNumber: formatted });
    const error = getPhoneNumberError(formatted);
    setPhoneError(error);
  };

  const handleSaveProfile = async () => {
    try {
      if (formData.phoneNumber) {
        const phoneValidationError = getPhoneNumberError(formData.phoneNumber);
        if (phoneValidationError) {
          setPhoneError(phoneValidationError);
          return;
        }
      }

      await userService.updateProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
      });
      showNotification('Profile updated successfully', 'success');
      setEditMode(false);
      fetchProfile();
    } catch (error) {
      console.error('Failed to update profile:', error);
      showNotification('Failed to update profile. Please try again.', 'error');
    }
  };

  const validatePassword = (): boolean => {
    const errors = {
      current: '',
      new: '',
      confirm: '',
    };

    if (!passwordData.currentPassword) {
      errors.current = 'Current password is required';
    }

    if (!passwordData.newPassword) {
      errors.new = 'New password is required';
    } else if (passwordData.newPassword.length < 8) {
      errors.new = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordData.newPassword)) {
      errors.new = 'Password must contain uppercase, lowercase, and number';
    }

    if (!passwordData.confirmPassword) {
      errors.confirm = 'Please confirm your new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirm = 'Passwords do not match';
    }

    setPasswordErrors(errors);
    return !errors.current && !errors.new && !errors.confirm;
  };

  const handleChangePassword = async () => {
    if (!validatePassword()) {
      return;
    }

    try {
      await userService.updatePassword(passwordData);
      showNotification('Password changed successfully', 'success');
      setPasswordDialogOpen(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setPasswordErrors({ current: '', new: '', confirm: '' });
    } catch (error) {
      console.error('Failed to change password:', error);
      showNotification('Failed to change password. Please check your current password.', 'error');
    }
  };

  const handleToggle2FA = async (enabled: boolean) => {
    try {
      await userService.toggle2FA(enabled);
      showNotification(
        enabled ? '2FA enabled successfully' : '2FA disabled successfully',
        'success'
      );
      fetchProfile();
    } catch (error) {
      console.error('Failed to toggle 2FA:', error);
      showNotification('Failed to update 2FA settings. Please try again.', 'error');
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return <AdminPanelSettings fontSize="small" />;
      default:
        return <BadgeIcon fontSize="small" />;
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    if (profile) {
      setFormData({
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        phoneNumber: profile.phoneNumber || '',
      });
    }
    setPhoneError(null);
  };

  if (!profile) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Typography>Loading profile...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">My Profile</Typography>
        <Box display="flex" gap={2}>
          {!editMode ? (
            <>
              <Button variant="outlined" startIcon={<Lock />} onClick={() => setPasswordDialogOpen(true)}>
                Change Password
              </Button>
              <Button variant="contained" startIcon={<Edit />} onClick={() => setEditMode(true)}>
                Edit Profile
              </Button>
            </>
          ) : (
            <>
              <Button startIcon={<Cancel />} onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveProfile}
                disabled={!!phoneError}
              >
                Save Changes
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Profile Information */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Profile Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  disabled={!editMode}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  disabled={!editMode}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={!editMode}
                  required
                  InputProps={{
                    endAdornment: profile.emailVerified ? (
                      <Verified color="success" fontSize="small" />
                    ) : null,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.phoneNumber}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  disabled={!editMode}
                  error={!!phoneError}
                  helperText={phoneError || 'Format: +263712345678'}
                  placeholder="+263712345678"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Username"
                  value={profile.username}
                  disabled
                  helperText="Username cannot be changed"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Security Settings */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Security Settings
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <List>
              <ListItem>
                <ListItemAvatar>
                  <Avatar>
                    <Security />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Two-Factor Authentication"
                  secondary={
                    profile.twoFactorEnabled
                      ? 'Additional security is enabled for your account'
                      : 'Add an extra layer of security to your account'
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={profile.twoFactorEnabled || false}
                      onChange={(e) => handleToggle2FA(e.target.checked)}
                    />
                  }
                  label=""
                />
              </ListItem>
              <ListItem>
                <ListItemAvatar>
                  <Avatar>
                    <Lock />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Password"
                  secondary="Change your password regularly to keep your account secure"
                />
                <Button variant="outlined" onClick={() => setPasswordDialogOpen(true)}>
                  Change
                </Button>
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Profile Summary */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  bgcolor: 'primary.main',
                  fontSize: '3rem',
                  mb: 2,
                }}
              >
                {profile.firstName[0]}
                {profile.lastName[0]}
              </Avatar>
              <Typography variant="h5" gutterBottom>
                {profile.firstName} {profile.lastName}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                @{profile.username}
              </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <List dense>
              <ListItem>
                <ListItemAvatar>
                  <Email fontSize="small" color="action" />
                </ListItemAvatar>
                <ListItemText
                  primary="Email"
                  secondary={profile.email}
                  secondaryTypographyProps={{ sx: { wordBreak: 'break-word' } }}
                />
              </ListItem>
              {profile.phoneNumber && (
                <ListItem>
                  <ListItemAvatar>
                    <Phone fontSize="small" color="action" />
                  </ListItemAvatar>
                  <ListItemText primary="Phone" secondary={profile.phoneNumber} />
                </ListItem>
              )}
              {profile.partnerName && (
                <ListItem>
                  <ListItemAvatar>
                    <Business fontSize="small" color="action" />
                  </ListItemAvatar>
                  <ListItemText
                    primary="Organization"
                    secondary={profile.partnerName}
                  />
                </ListItem>
              )}
            </List>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Roles
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" mb={2}>
              {profile.roles.map((role) => (
                <Chip
                  key={role}
                  label={role}
                  size="small"
                  icon={getRoleIcon(role)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Stack>

            {profile.lastLogin && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="textSecondary">
                  Last login: {format(new Date(profile.lastLogin), 'MMM dd, yyyy HH:mm')}
                </Typography>
              </>
            )}
          </Paper>

          {!profile.emailVerified && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Your email is not verified. Please check your inbox for a verification link.
            </Alert>
          )}
        </Grid>
      </Grid>

      {/* Change Password Dialog */}
      <Dialog
        open={passwordDialogOpen}
        onClose={() => {
          setPasswordDialogOpen(false);
          setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
          setPasswordErrors({ current: '', new: '', confirm: '' });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              type="password"
              label="Current Password"
              value={passwordData.currentPassword}
              onChange={(e) =>
                setPasswordData({ ...passwordData, currentPassword: e.target.value })
              }
              error={!!passwordErrors.current}
              helperText={passwordErrors.current}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="password"
              label="New Password"
              value={passwordData.newPassword}
              onChange={(e) =>
                setPasswordData({ ...passwordData, newPassword: e.target.value })
              }
              error={!!passwordErrors.new}
              helperText={
                passwordErrors.new ||
                'Must be at least 8 characters with uppercase, lowercase, and number'
              }
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="password"
              label="Confirm New Password"
              value={passwordData.confirmPassword}
              onChange={(e) =>
                setPasswordData({ ...passwordData, confirmPassword: e.target.value })
              }
              error={!!passwordErrors.confirm}
              helperText={passwordErrors.confirm}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPasswordDialogOpen(false);
              setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
              setPasswordErrors({ current: '', new: '', confirm: '' });
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleChangePassword}>
            Change Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ ...notification, open: false })}
      />
    </Box>
  );
};

export default UserProfile;
